/**
 * SoleadLab 2.0 — Auth Store
 *
 * Centralized authentication state. Replaces scattered localStorage reads.
 * Uses Zustand persist middleware for durable sessions.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  role: string;
  clientId?: string;
  displayName?: string;
}

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  role: string;
}

/**
 * When an admin impersonates another user, we stash the admin's session here
 * so they can pop back without re-entering credentials. The banner in
 * EditorPage reads `impersonator !== null` to show the "Return to admin" UI.
 */
export interface ImpersonatorSnapshot {
  token: string;
  user: AuthUser;
  currentOrg: OrgSummary | null;
  orgs: OrgSummary[];
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  currentOrg: OrgSummary | null;
  orgs: OrgSummary[];
  isAuthenticated: boolean;
  impersonator: ImpersonatorSnapshot | null;

  login: (token: string, user: AuthUser, currentOrg?: OrgSummary | null, orgs?: OrgSummary[]) => void;
  logout: () => void;
  setOrgs: (orgs: OrgSummary[]) => void;
  switchOrg: (orgId: string) => void;
  setToken: (token: string) => void;
  startImpersonation: (token: string, user: AuthUser, currentOrg: OrgSummary | null, orgs: OrgSummary[]) => void;
  stopImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      currentOrg: null,
      orgs: [],
      isAuthenticated: false,
      impersonator: null,

      login: (token, user, currentOrg = null, orgs = []) => {
        set({
          token,
          user,
          currentOrg,
          orgs,
          isAuthenticated: true,
          // A regular login always exits any active impersonation.
          impersonator: null,
        });
      },

      logout: () => {
        set({
          token: null,
          user: null,
          currentOrg: null,
          orgs: [],
          isAuthenticated: false,
          impersonator: null,
        });
      },

      setOrgs: (orgs) => set({ orgs }),

      switchOrg: (orgId) => {
        const { orgs } = get();
        const org = orgs.find(o => o.id === orgId);
        if (org) set({ currentOrg: org });
      },

      setToken: (token) => set({ token }),

      startImpersonation: (token, user, currentOrg, orgs) => {
        const current = get();
        if (!current.token || !current.user) return;
        // Only stash a snapshot the first time — chained impersonation keeps
        // the original admin's session as the rewind target.
        const snapshot: ImpersonatorSnapshot = current.impersonator ?? {
          token: current.token,
          user: current.user,
          currentOrg: current.currentOrg,
          orgs: current.orgs,
        };
        set({
          token,
          user,
          currentOrg,
          orgs,
          isAuthenticated: true,
          impersonator: snapshot,
        });
      },

      stopImpersonation: () => {
        const { impersonator } = get();
        if (!impersonator) return;
        set({
          token: impersonator.token,
          user: impersonator.user,
          currentOrg: impersonator.currentOrg,
          orgs: impersonator.orgs,
          isAuthenticated: true,
          impersonator: null,
        });
      },
    }),
    {
      name: 'sl_auth',
      // Migrate from old localStorage keys on first load
      onRehydrateStorage: () => (state) => {
        if (state?.token) return; // Already has persisted state

        // Try to migrate from old sl_token / sl_user keys
        try {
          const oldToken = localStorage.getItem('sl_token');
          const oldUser = localStorage.getItem('sl_user');
          if (oldToken && oldUser) {
            const user = JSON.parse(oldUser) as AuthUser;
            useAuthStore.setState({
              token: oldToken,
              user,
              isAuthenticated: true,
            });
            // Clean up old keys
            localStorage.removeItem('sl_token');
            localStorage.removeItem('sl_user');
          }
        } catch {
          // Ignore migration errors
        }
      },
    }
  )
);
